import {
  Component, OnInit, Output, Input,
  EventEmitter, ElementRef, ViewChild, HostListener
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [FormsModule, CommonModule, TranslateModule],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  // 🔵/🟣 Thème : 'bridge' => bleu, 'swap' => violet
  @Input() mode: 'swap' | 'bridge' = 'swap';

  selectedLanguage: string = localStorage.getItem('lang') || 'en';
  selectedCurrency: string = localStorage.getItem('currency') || 'USD';
  languages: { code: string; label: string }[] = [];
  currencies = ['USD', 'EUR', 'GBP', 'CAD', 'INR', 'IDR', 'VND', 'TRY', 'NGN', 'BRL', 'ZAR', 'THB', 'MYR','PHP'];
  @Output() currencyChange = new EventEmitter<string>();

  // 🔽 Gestion du menu déroulant
  showSettingsMenu = true;
  @ViewChild('settingsMenuRef') settingsMenuRef!: ElementRef;

  constructor(private translate: TranslateService, private http: HttpClient, private router: Router) { }

  ngOnInit() {
    this.loadLanguages();

    if (!localStorage.getItem('lang')) {
      const browserLang = navigator.language.split('-')[0];
      const availableLangs = this.languages.map(l => l.code);
      if (availableLangs.includes(browserLang)) this.changeLanguage(browserLang);
      else this.changeLanguage('en');
    }
  }

  toggleSettings(): void {
    this.showSettingsMenu = !this.showSettingsMenu;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const clickedInside = this.settingsMenuRef?.nativeElement.contains(event.target);
    if (!clickedInside && this.showSettingsMenu) {
      this.showSettingsMenu = false;
    }
  }

  loadLanguages() {
    const languageFiles = [
      'en.json', 'es.json', 'fr.json', 'pt.json', 'it.json',
      'hi.json', 'bn.json', 'ta.json', 'te.json', 'mr.json',
      'id.json', 'vi.json', 'tr.json', 'km.json',
      'th.json', 'ms.json', 'tl.json', 'zu.json', 'af.json'
    ];

    const labels: Record<string, string> = {
      en: 'English',
      es: 'Español',
      fr: 'Français',
      pt: 'Português',
      it: 'Italiano',
      hi: 'हिन्दी',
      bn: 'বাংলা',
      ta: 'தமிழ்',
      te: 'తెలుగు',
      mr: 'मराठी',
      id: 'Bahasa Indonesia',
      vi: 'Tiếng Việt',
      tr: 'Türkçe',
      km: 'ខ្មែរ',
      th: 'ไทย',              // Thai
      ms: 'Bahasa Melayu',     // Malay (Malaysia)
      tl: 'Filipino', 
      zu: 'isiZulu',           // Zulu (South Africa)
      af: 'Afrikaans' 
    };

    this.languages = languageFiles.map(file => {
      const code = file.replace('.json', '');
      return { code, label: labels[code] || code };
    });

    if (!this.languages.some(lang => lang.code === this.selectedLanguage)) {
      this.selectedLanguage = this.languages[0]?.code || 'en';
      this.changeLanguage(this.selectedLanguage);
    }
  }


  changeCurrency(code: string) {
    this.selectedCurrency = code;
    localStorage.setItem('currency', code);
    this.currencyChange.emit(code);
  }

  changeLanguage(lang: string) {
    // 1) i18n runtime + persistance
    this.translate.use(lang);
    localStorage.setItem('lang', lang);
    this.selectedLanguage = lang;
    document.documentElement.setAttribute('lang', lang);

    // 2) Analyse de l’URL courante
    const tree = this.router.parseUrl(this.router.url);
    const primary = tree.root.children['primary'];
    const segs = primary ? primary.segments.map(s => s.path) : [];

    const supported = this.languages.map(l => l.code); // ex: ['en','fr',...]
    const langless = new Set(['', 'bridge', 'alerts', 'a-propos', 'disclaimer']);

    // Rien à naviguer pour la home ('') → pas de préfixe :lang
    if (segs.length === 0) {
      return; // on reste sur '/', on a déjà changé la langue de l'app
    }

    const first = segs[0];

    // Cas A) La route est déjà préfixée par une langue supportée → remplacer ce segment
    if (supported.includes(first)) {
      if (first !== lang) {
        const next = [lang, ...segs.slice(1)];
        this.router.navigate(['/', ...next], {
          queryParamsHandling: 'preserve',
          preserveFragment: true,
          replaceUrl: true,
        });
      }
      return;
    }

    // Cas B) Routes "langless" → ne pas injecter de :lang (sinon 404 → fallback swap)
    if (langless.has(first)) {
      // Optionnel: forcer une renavigation “no-op” pour rafraîchir les pipes si nécessaire
      this.router.navigate(['/', ...segs], {
        queryParamsHandling: 'preserve',
        preserveFragment: true,
        replaceUrl: true,
      });
      return;
    }

    // Cas C) Routes SEO qui attendent ':locale/:slug'
    // Exemple: /fr/usdt-to-eth  (2 segments mini) → on remplace juste le 1er par la nouvelle langue
    if (segs.length >= 2) {
      const next = [lang, ...segs.slice(1)];
      this.router.navigate(['/', ...next], {
        queryParamsHandling: 'preserve',
        preserveFragment: true,
        replaceUrl: true,
      });
      return;
    }

    // Fallback: si on tombe sur un cas inattendu, on n’injecte rien
  }


  isDarkMode(): boolean {
    return document.body.classList.contains('dark-mode');
  }

  getCurrencyLabel(symbol?: string): string {
    if (!symbol) return '';
    const key = 'CURRENCIES.' + symbol.toUpperCase();
    const translated = this.translate.instant(key);
    return translated !== key ? translated : symbol;
  }
}
